import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ruler, ChevronRight, Globe, HelpCircle, Phone, Mail } from 'lucide-react';
import useStore from '../store/useStore';

const SizeGuidePage = () => {
  const { language } = useStore();
  const [activeLang, setActiveLang] = useState(language === 'nl' ? 'nl' : 'fr');

  const content = {
    fr: {
      title: 'Guide de prise de mesures',
      subtitle: 'Choisissez la bonne taille pour vos achats sur EBB-Shop',
      lastUpdate: '30 janvier 2026',
      beforeStart: 'Avant de commencer',
      beforeStartItems: [
        'Utilisez un mètre ruban souple (de couturière).',
        'Prenez vos mesures en sous‑vêtements ou sur des vêtements légers et ajustés.',
        'Tenez‑vous droit(e) et détendu(e), respirez normalement.',
        'Le mètre doit être bien à plat contre le corps, parallèle au sol, sans serrer ni être trop lâche.',
        'Notez vos mesures en centimètres (cm).',
        'Si possible, demandez à quelqu\'un de vous aider ou utilisez un miroir.'
      ],
      mainMeasures: 'Les 3 mesures principales',
      chest: {
        title: '1. Tour de poitrine',
        steps: [
          'Placez le mètre ruban autour de la partie la plus forte de la poitrine (au niveau des tétons pour les hommes, au niveau de la pointe des seins pour les femmes).',
          'Passez le mètre sous les aisselles et à plat sur le dos.',
          'Le mètre doit être horizontal tout autour du corps.',
          'Ne retenez pas votre respiration, restez naturel(le).'
        ],
        tips: [
          'Pour les femmes : portez un soutien‑gorge habituel (non push‑up) si c\'est pour un vêtement ajusté.',
          'Pour les hommes : mesurez au niveau des pectoraux.'
        ]
      },
      waist: {
        title: '2. Tour de taille',
        steps: [
          'Repérez votre taille naturelle : c\'est la partie la plus étroite du torse, généralement au niveau du nombril ou juste au‑dessus.',
          'Astuce : penchez‑vous légèrement sur le côté, le pli qui se forme indique votre taille naturelle.',
          'Placez le mètre autour de la taille, bien horizontal, sans serrer.',
          'Ne rentrez pas le ventre.'
        ],
        tip: 'Nouez un élastique ou un ruban autour de votre taille pour bien marquer l\'emplacement avant de mesurer.'
      },
      hips: {
        title: '3. Tour de hanches',
        steps: [
          'Mesurez autour de la partie la plus large des hanches et des fesses.',
          'Le mètre doit passer horizontalement tout autour du corps.',
          'Tenez‑vous les pieds joints, détendu(e).'
        ],
        tip: 'C\'est souvent environ 20 cm en dessous de la taille naturelle, mais cela varie selon la morphologie.'
      },
      additionalMeasures: 'Mesures supplémentaires (selon les vêtements)',
      armLength: {
        title: 'Longueur de bras (manche)',
        steps: [
          'Pliez légèrement le bras.',
          'Mesurez du centre de la nuque, en passant par l\'épaule et le coude, jusqu\'au poignet.'
        ]
      },
      inseam: {
        title: 'Entrejambe (longueur de jambe intérieure)',
        steps: [
          'Mesurez depuis l\'entre‑jambes jusqu\'au bas de la cheville (ou jusqu\'à l\'endroit souhaité pour l\'ourlet).',
          'Portez un pantalon bien ajusté pour faciliter la mesure.'
        ]
      },
      backLength: {
        title: 'Longueur du dos',
        steps: [
          'Mesurez depuis la base du cou (vertèbre proéminente) jusqu\'à la taille naturelle.'
        ]
      },
      sizeChart: 'Tableau de correspondance des tailles (indicatif)',
      sizeChartNote: 'Les tailles peuvent varier selon les marques. Consultez toujours le guide des tailles spécifique au produit sur la fiche article.',
      childrenTips: 'Conseils pour les enfants',
      childrenTipsItems: [
        'Les enfants bougent beaucoup : mesurez‑les debout contre un mur si possible.',
        'Prenez les mesures deux fois pour vérifier.',
        'Pour les tout‑petits, ajoutez 2‑3 cm de marge pour le confort et la croissance.',
        'Référez‑vous à la taille (hauteur en cm) et à l\'âge indiqués sur les fiches produits.'
      ],
      doubt: 'En cas de doute',
      doubtItems: [
        'Si vous êtes entre deux tailles, choisissez la taille supérieure pour plus de confort.',
        'Consultez les avis clients sur la fiche produit pour savoir si l\'article taille petit ou grand.',
        'Contactez notre service client pour obtenir de l\'aide.'
      ],
      returnReminder: 'Droit de rétractation (rappel B2C)',
      returnReminderText: 'Vous disposez de 14 jours pour retourner un article qui ne conviendrait pas.',
      seeReturnPolicy: 'Voir notre Politique de retour'
    },
    en: {
      title: 'Measurement Guide',
      subtitle: 'Choose the right size for your purchases on EBB-Shop',
      lastUpdate: 'January 30, 2026',
      beforeStart: 'Before you start',
      beforeStartItems: [
        'Use a soft measuring tape (tailor\'s tape).',
        'Take measurements in underwear or over thin, fitted clothing.',
        'Stand straight and relaxed, breathe normally.',
        'The tape should be flat against your body, parallel to the floor, neither too tight nor too loose.',
        'Record measurements in centimeters (cm).',
        'If possible, ask someone to help you or use a mirror.'
      ],
      mainMeasures: 'The 3 main measurements',
      chest: {
        title: '1. Chest circumference',
        steps: [
          'Place the tape around the fullest part of the chest (at nipple level for men, at bust point for women).',
          'Pass the tape under the arms and flat across the back.',
          'The tape must be horizontal all around the body.',
          'Don\'t hold your breath, stay natural.'
        ],
        tips: [
          'For women: wear your usual bra (non‑push‑up) if measuring for fitted garments.',
          'For men: measure at chest/pectoral level.'
        ]
      },
      waist: {
        title: '2. Waist circumference',
        steps: [
          'Locate your natural waist: the narrowest part of your torso, usually at or just above the belly button.',
          'Tip: bend slightly to the side, the crease that forms indicates your natural waist.',
          'Place the tape around the waist, perfectly horizontal, without squeezing.',
          'Don\'t suck in your stomach.'
        ],
        tip: 'Tie an elastic band or ribbon around your waist to mark the location before measuring.'
      },
      hips: {
        title: '3. Hip circumference',
        steps: [
          'Measure around the widest part of the hips and buttocks.',
          'The tape must pass horizontally all around the body.',
          'Stand with feet together, relaxed.'
        ],
        tip: 'This is often about 20 cm below the natural waist, but varies by body type.'
      },
      additionalMeasures: 'Additional measurements (depending on garments)',
      armLength: {
        title: 'Arm length (sleeve)',
        steps: [
          'Bend your arm slightly.',
          'Measure from the center of the back of the neck, passing over the shoulder and elbow, down to the wrist.'
        ]
      },
      inseam: {
        title: 'Inseam (inside leg length)',
        steps: [
          'Measure from the crotch to the bottom of the ankle (or to the desired hem position).',
          'Wear well‑fitted pants to facilitate measurement.'
        ]
      },
      backLength: {
        title: 'Back length',
        steps: [
          'Measure from the base of the neck (prominent vertebra) to the natural waist.'
        ]
      },
      sizeChart: 'Size conversion chart (indicative)',
      sizeChartNote: 'Sizes may vary by brand. Always consult the specific size guide on the product page.',
      childrenTips: 'Tips for children',
      childrenTipsItems: [
        'Children move a lot: measure them standing against a wall if possible.',
        'Take measurements twice to verify.',
        'For toddlers, add 2‑3 cm margin for comfort and growth.',
        'Refer to height (in cm) and age indicated on product pages.'
      ],
      doubt: 'When in doubt',
      doubtItems: [
        'If you\'re between two sizes, choose the larger size for more comfort.',
        'Check customer reviews on the product page to see if the item runs small or large.',
        'Contact our customer service for assistance.'
      ],
      returnReminder: 'Right of withdrawal (B2C reminder)',
      returnReminderText: 'You have 14 days to return an item that doesn\'t fit.',
      seeReturnPolicy: 'See our Return Policy'
    },
    nl: {
      title: 'Maatgids',
      subtitle: 'Kies de juiste maat voor uw aankopen op EBB-Shop',
      lastUpdate: '30 januari 2026',
      beforeStart: 'Voordat u begint',
      beforeStartItems: [
        'Gebruik een soepele meetlint (kleermakerslint).',
        'Neem maten in ondergoed of over dunne, nauwsluitende kleding.',
        'Sta recht en ontspannen, adem normaal.',
        'Het meetlint moet plat tegen het lichaam liggen, evenwijdig aan de grond, niet te strak en niet te los.',
        'Noteer maten in centimeters (cm).',
        'Vraag indien mogelijk iemand om te helpen of gebruik een spiegel.'
      ],
      mainMeasures: 'De 3 belangrijkste maten',
      chest: {
        title: '1. Borstomvang',
        steps: [
          'Plaats het meetlint rond het breedste deel van de borst (ter hoogte van de tepels voor mannen, ter hoogte van de buste voor vrouwen).',
          'Laat het lint onder de oksels en plat over de rug lopen.',
          'Het meetlint moet horizontaal rond het lichaam lopen.',
          'Houd uw adem niet in, blijf natuurlijk.'
        ],
        tips: [
          'Voor vrouwen: draag een gewone beha (geen push‑up) bij het meten voor nauwsluitende kledingstukken.',
          'Voor mannen: meet ter hoogte van de borstspieren.'
        ]
      },
      waist: {
        title: '2. Tailleomvang',
        steps: [
          'Lokaliseer uw natuurlijke taille: het smalste deel van uw romp, meestal ter hoogte van of net boven de navel.',
          'Tip: buig licht opzij, de plooi die ontstaat geeft uw natuurlijke taille aan.',
          'Plaats het meetlint rond de taille, perfect horizontaal, zonder te knijpen.',
          'Trek uw buik niet in.'
        ],
        tip: 'Bind een elastiek of lint rond uw taille om de locatie te markeren voordat u meet.'
      },
      hips: {
        title: '3. Heupomvang',
        steps: [
          'Meet rond het breedste deel van de heupen en billen.',
          'Het meetlint moet horizontaal rond het lichaam lopen.',
          'Sta met voeten tegen elkaar, ontspannen.'
        ],
        tip: 'Dit is vaak ongeveer 20 cm onder de natuurlijke taille, maar varieert per lichaamsbouw.'
      },
      additionalMeasures: 'Extra maten (afhankelijk van kledingstukken)',
      armLength: {
        title: 'Armlengte (mouw)',
        steps: [
          'Buig uw arm licht.',
          'Meet vanaf het midden van de nek, over de schouder en elleboog, tot aan de pols.'
        ]
      },
      inseam: {
        title: 'Binnenbeenlengte',
        steps: [
          'Meet vanaf het kruis tot de onderkant van de enkel (of tot de gewenste zoomlengte).',
          'Draag een goed passende broek om het meten te vergemakkelijken.'
        ]
      },
      backLength: {
        title: 'Ruglengte',
        steps: [
          'Meet vanaf de basis van de nek (uitstekende wervel) tot de natuurlijke taille.'
        ]
      },
      sizeChart: 'Maattabel (indicatief)',
      sizeChartNote: 'Maten kunnen per merk variëren. Raadpleeg altijd de specifieke maatgids op de productpagina.',
      childrenTips: 'Tips voor kinderen',
      childrenTipsItems: [
        'Kinderen bewegen veel: meet ze staand tegen een muur indien mogelijk.',
        'Neem maten twee keer om te controleren.',
        'Voor peuters, voeg 2‑3 cm marge toe voor comfort en groei.',
        'Raadpleeg de lichaamslengte (in cm) en leeftijd vermeld op productpagina\'s.'
      ],
      doubt: 'Bij twijfel',
      doubtItems: [
        'Als u tussen twee maten zit, kies dan de grotere maat voor meer comfort.',
        'Bekijk klantbeoordelingen op de productpagina om te zien of het artikel klein of groot uitvalt.',
        'Neem contact op met onze klantenservice voor hulp.'
      ],
      returnReminder: 'Herroepingsrecht (B2C herinnering)',
      returnReminderText: 'U heeft 14 dagen om een artikel dat niet past terug te sturen.',
      seeReturnPolicy: 'Zie ons Retourbeleid'
    }
  };

  const t = content[activeLang];

  const sizeChartData = [
    { size: 'XS', chest: '78-82', waist: '58-62', hips: '84-88' },
    { size: 'S', chest: '82-86', waist: '62-66', hips: '88-92' },
    { size: 'M', chest: '86-90', waist: '66-70', hips: '92-96' },
    { size: 'L', chest: '90-94', waist: '70-74', hips: '96-100' },
    { size: 'XL', chest: '94-98', waist: '74-78', hips: '100-104' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="size-guide-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/shop" className="hover:text-amber-600">
            {activeLang === 'fr' ? 'Accueil' : 'Home'}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900">{t.title}</span>
        </div>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Ruler className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
                <p className="text-gray-500 text-sm">{t.subtitle}</p>
              </div>
            </div>

            {/* Language Selector */}
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <div className="flex rounded-lg border overflow-hidden">
                {['fr', 'en', 'nl'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      activeLang === lang
                        ? 'bg-amber-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            {activeLang === 'fr' ? 'Dernière mise à jour' : activeLang === 'nl' ? 'Laatst bijgewerkt' : 'Last updated'}: {t.lastUpdate}
          </p>
        </div>

        {/* Before you start */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            {t.beforeStart}
          </h2>
          <ul className="space-y-2">
            {t.beforeStartItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-amber-800">
                <span className="text-amber-600 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Main Measurements */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t.mainMeasures}</h2>

          {/* Chest */}
          <div className="mb-8 pb-8 border-b">
            <h3 className="text-lg font-semibold text-amber-700 mb-4">{t.chest.title}</h3>
            <ul className="space-y-2 mb-4">
              {t.chest.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-700">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {activeLang === 'fr' ? 'Conseils :' : activeLang === 'nl' ? 'Tips:' : 'Tips:'}
              </p>
              {t.chest.tips.map((tip, idx) => (
                <p key={idx} className="text-sm text-gray-600">• {tip}</p>
              ))}
            </div>
          </div>

          {/* Waist */}
          <div className="mb-8 pb-8 border-b">
            <h3 className="text-lg font-semibold text-amber-700 mb-4">{t.waist.title}</h3>
            <ul className="space-y-2 mb-4">
              {t.waist.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-700">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>{activeLang === 'fr' ? 'Astuce pratique :' : activeLang === 'nl' ? 'Praktische tip:' : 'Practical tip:'}</strong> {t.waist.tip}
              </p>
            </div>
          </div>

          {/* Hips */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-amber-700 mb-4">{t.hips.title}</h3>
            <ul className="space-y-2 mb-4">
              {t.hips.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-700">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>{activeLang === 'fr' ? 'Conseil :' : activeLang === 'nl' ? 'Tip:' : 'Tip:'}</strong> {t.hips.tip}
              </p>
            </div>
          </div>
        </div>

        {/* Additional Measurements */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t.additionalMeasures}</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Arm Length */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t.armLength.title}</h3>
              <ul className="space-y-1">
                {t.armLength.steps.map((step, idx) => (
                  <li key={idx} className="text-sm text-gray-600">• {step}</li>
                ))}
              </ul>
            </div>

            {/* Inseam */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t.inseam.title}</h3>
              <ul className="space-y-1">
                {t.inseam.steps.map((step, idx) => (
                  <li key={idx} className="text-sm text-gray-600">• {step}</li>
                ))}
              </ul>
            </div>

            {/* Back Length */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t.backLength.title}</h3>
              <ul className="space-y-1">
                {t.backLength.steps.map((step, idx) => (
                  <li key={idx} className="text-sm text-gray-600">• {step}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Size Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.sizeChart}</h2>
          <p className="text-sm text-gray-500 mb-6">{t.sizeChartNote}</p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-amber-100">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-amber-900">
                    {activeLang === 'fr' ? 'Taille' : activeLang === 'nl' ? 'Maat' : 'Size'}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-amber-900">
                    {activeLang === 'fr' ? 'Tour de poitrine (cm)' : activeLang === 'nl' ? 'Borstomvang (cm)' : 'Chest (cm)'}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-amber-900">
                    {activeLang === 'fr' ? 'Tour de taille (cm)' : activeLang === 'nl' ? 'Tailleomvang (cm)' : 'Waist (cm)'}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-amber-900">
                    {activeLang === 'fr' ? 'Tour de hanches (cm)' : activeLang === 'nl' ? 'Heupomvang (cm)' : 'Hips (cm)'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sizeChartData.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{row.size}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{row.chest}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{row.waist}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{row.hips}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tips for children */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t.childrenTips}</h2>
          <ul className="space-y-2">
            {t.childrenTipsItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-gray-700">
                <span className="text-amber-600 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* When in doubt */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-blue-900 mb-4">{t.doubt}</h2>
          <ul className="space-y-2 mb-4">
            {t.doubtItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-blue-800">
                <span className="text-blue-600 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-4 text-sm text-blue-800">
            <a href="mailto:admin@shop-ebb.com" className="flex items-center gap-1 hover:underline">
              <Mail className="w-4 h-4" />
              admin@shop-ebb.com
            </a>
            <span className="flex items-center gap-1">
              <Phone className="w-4 h-4" />
              +32 470 00 00 00
            </span>
          </div>
        </div>

        {/* Return reminder */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-green-900 mb-2">{t.returnReminder}</h2>
          <p className="text-green-800 mb-3">{t.returnReminderText}</p>
          <Link 
            to="/legal/returns" 
            className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 font-medium"
          >
            {t.seeReturnPolicy}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SizeGuidePage;
